import React from 'react';
import { motion } from 'framer-motion';

const WithoutFlow = [
  'Detect',
  'Notify',
  'Wait',
  'Ignored (91%)',
  'Reappears (90%)',
  'Repeat',
];

const WithFlow = [
  'Dope',
  'Detect',
  'Identify source',
  'Revoke access',
  'Stream dies',
];

const Arrow = ({ muted }) => (
  <svg
    className={`flex-shrink-0 w-4 h-4 ${muted ? 'text-white/25' : 'text-teal/70'}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const Section05Contrast = () => {
  return (
    <section className="section-pad section-glass" id="contrast">
      <div className="container-edge">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col gap-6 mb-20 md:mb-24 max-w-4xl"
        >
          <h2 className="font-display font-bold text-white leading-[0.95] tracking-tight text-section">
            Same threat.<br />
            <span className="text-teal">Different maths.</span>
          </h2>
        </motion.div>

        {/* Without */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative py-10 md:py-14 border-t border-white/5"
        >
          <div className="grid grid-cols-12 gap-6 items-start">
            <div className="col-span-12 md:col-span-3 space-y-2">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-coral/80">
                Without source-level protection
              </p>
              <h3 className="font-display text-2xl md:text-3xl text-white/80 font-medium">
                Chase. Fail. Repeat.
              </h3>
            </div>
            <div className="col-span-12 md:col-span-9 space-y-6">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-3 text-lg md:text-xl font-display font-light">
                {WithoutFlow.map((step, i) => (
                  <React.Fragment key={step}>
                    <span className={i > 2 ? 'text-coral' : 'text-white/55'}>
                      {step}
                    </span>
                    {i < WithoutFlow.length - 1 && <Arrow muted />}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-10 gap-y-3 pt-4 border-t border-white/5">
                <p className="text-white/60 text-sm">
                  <span className="block text-[0.65rem] font-mono uppercase tracking-[0.35em] text-white/35 mb-1">
                    Cost per notice
                  </span>
                  <span className="text-white text-lg">$500 – $2,000</span>
                </p>
                <p className="text-white/60 text-sm">
                  <span className="block text-[0.65rem] font-mono uppercase tracking-[0.35em] text-white/35 mb-1">
                    Success rate
                  </span>
                  <span className="text-coral text-lg font-semibold">5%</span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* With */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="relative py-10 md:py-14 border-t border-white/5"
        >
          <div className="grid grid-cols-12 gap-6 items-start">
            <div className="col-span-12 md:col-span-3 space-y-2">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-teal">
                With Kinesight
              </p>
              <h3 className="font-display text-2xl md:text-3xl text-white font-medium">
                Own. Identify. Kill.
              </h3>
            </div>
            <div className="col-span-12 md:col-span-9 space-y-6">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-3 text-lg md:text-xl font-display">
                {WithFlow.map((step, i) => (
                  <React.Fragment key={step}>
                    <span className={i === WithFlow.length - 1 ? 'text-teal font-semibold' : 'text-white'}>
                      {step}
                    </span>
                    {i < WithFlow.length - 1 && <Arrow />}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-10 gap-y-3 pt-4 border-t border-white/5">
                <p className="text-white/60 text-sm">
                  <span className="block text-[0.65rem] font-mono uppercase tracking-[0.35em] text-white/35 mb-1">
                    Cost
                  </span>
                  <span className="text-white text-lg">Fraction of enforcement spend</span>
                </p>
                <p className="text-white/60 text-sm">
                  <span className="block text-[0.65rem] font-mono uppercase tracking-[0.35em] text-white/35 mb-1">
                    Operation
                  </span>
                  <span className="text-teal text-lg font-semibold">Automated · Continuous</span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Revenue climax */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
          className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-end"
        >
          <div className="md:col-span-5 space-y-6">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-teal">
              The revenue case
            </p>
            <p className="text-xl md:text-2xl text-white/90 font-normal leading-[1.45]">
              LaLiga achieved a{' '}
              <span className="text-white font-semibold">60% piracy reduction</span>{' '}
              with source-level protection.
              <br />
              <br />
              At industry scale, even a{' '}
              <span className="text-white font-semibold">9% conversion</span>{' '}
              of defeated pirates means…
            </p>
          </div>

          <div className="md:col-span-7 flex flex-col items-start md:items-end">
            <h3
              className="stat-number text-teal"
              style={{ fontSize: 'clamp(4.5rem, 13vw, 12rem)' }}
            >
              $1.08B
            </h3>
            <p className="text-white/70 text-lg md:text-xl max-w-md md:text-right">
              in recovered revenue. Annually, industry-wide.
              <br />
              <span className="text-mist-500 font-mono text-xs uppercase tracking-[0.3em]">
                Current anti-piracy spend: ~$150M
              </span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Section05Contrast;
